import React, { useMemo } from 'react';
import { ReactSelectMenu } from '../../../../Dropdown/StyledReactSelect';
import { components, MultiValueGenericProps, OptionProps } from 'react-select';
import { IVirtualEventFieldsFragment } from '../../../../../utils/apollo/graphqlSchema';
import { colors } from '../../../../../config/styles';
import BuilderInfo from '../../../../BuilderInfo';
import { VersionedEntity } from '../../../../EntityLabels';

interface IProps {
  virtualEventOptions: IVirtualEventFieldsFragment[];
  selected: any;
  onChange: (selectedOption: any, isVirtual: boolean) => void;
  searchText: string;
  onSearchChange: (searchText: string) => void;
  isError?: boolean;
  isLoading?: boolean;
  blurInputOnSelect?: boolean;
  isDisabled?: boolean;
}

export default ({
  virtualEventOptions,
  selected,
  onChange,
  searchText,
  onSearchChange,
  isError = false,
  isLoading = false,
  blurInputOnSelect,
  isDisabled,
}: IProps) => {
  const groupOptions = useMemo(
    () =>
      virtualEventOptions.map((option) => ({
        ...option,
        // value is needed for react-select to work
        value: option.id,
        // label is needed for search in react-select
        label: option.name,
      })),
    [virtualEventOptions],
  );

  // this function is used to add required field for react-select.
  // without the value field, react-select will display the selected item in the menu option.
  const parsedSelected = useMemo(
    () =>
      selected.map((item: any) => ({
        ...item,
        // virtual event only uses id
        value: item.id,
        // label is needed for search in react-select
        label: item.name,
      })),
    [selected],
  );

  const onClickToRemoveItem = (clickedValue: string) => {
    const newSelected = parsedSelected.filter(
      (item: any) => item.value !== clickedValue,
    );
    onSelectedOptionsChange(newSelected);
  };

  const renderMultiValueLabel = (
    props: MultiValueGenericProps<IVirtualEventFieldsFragment, true>,
  ) => {
    const { data } = props;

    return (
      <VersionedEntity
        label={data.name}
        onClear={() => onClickToRemoveItem(data.value)}
        isDisabled={data.isDisabled}
        isLatestVersion={data.isLatestVersion}
      >
        <BuilderInfo kind="virtualEvent" virtualEvent={data} />
      </VersionedEntity>
    );
  };

  const Option = (props: OptionProps<IVirtualEventFieldsFragment, true>) => {
    const data = props.data;
    return (
      <div key={data.name}>
        <components.Option {...props}>{data.name}</components.Option>
      </div>
    );
  };

  const onSelectedOptionsChange = (newOptions: any) => {
    if (newOptions.length > 1) {
      newOptions = [newOptions[1]];
    }
    onChange(newOptions, true);
  };

  return (
    <ReactSelectMenu
      isDisabled={isDisabled}
      closeMenuOnSelect
      onChange={onSelectedOptionsChange}
      value={parsedSelected}
      blurInputOnSelect={blurInputOnSelect}
      isMulti
      isLoading={isLoading}
      options={groupOptions}
      inputValue={searchText}
      onInputChange={onSearchChange}
      components={{
        MultiValueLabel: renderMultiValueLabel,
        Option,
      }}
      styles={{
        control: (base) => {
          if (isError) {
            return {
              ...base,
              borderColor: colors.mainError,
            };
          }

          return base;
        },
      }}
    />
  );
};
